import {
  AppLayout,
  Flashbar,
  SideNavigation,
} from '@cloudscape-design/components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getApp } from 'firebase/app';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import React, { useState } from 'react';
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Toaster } from 'sonner';

import Breadcrumbs from './components/Breadcrumbs';
import Navigation from './components/Navigation';
import VersionCheck from './components/VersionCheck';
import NotificationsProvider, { useNotifications } from './features/notifications';
import LoginProvider from './providers/loginProvider';
import UserDataProvider, { useUserData } from './providers/userDataProvider';

const queryClient = new QueryClient();

function App() {
  const { user } = useUserData();

  // for notifications
  const { notifications } = useNotifications();

  // for side navigation
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);

  return (
    <>
      <Toaster richColors />
      <VersionCheck />
      <ScrollRestoration />
      <Navigation />
      <div id={location.pathname.replaceAll('/', '-')}>
        <AppLayout
          toolsHide
          breadcrumbs={<Breadcrumbs />}
          content={<Outlet />}
          notifications={notifications.length > 0
            ? (
              <Flashbar
                items={notifications.filter(
                  (notification) => notification.location === location.pathname,
                )}
              />
            ) : null}
          navigationOpen={navigationOpen}
          onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
          navigation={(user?.email || '').includes('parallelfluidics.com') ? (
            <SideNavigation
              activeHref={location}
              onFollow={(event) => {
                if (!event.detail.external) {
                  event.preventDefault();
                  navigate(event.detail.href);
                  setNavigationOpen(false);
                }
              }}
              items={[
                {
                  type: 'section-group',
                  title: 'HMI',
                  items: [
                    {
                      type: 'link',
                      text: 'Press 1',
                      href: '/live/press1',
                    },
                    {
                      type: 'link',
                      text: 'Press 2',
                      href: '/live/press2',
                    },
                    {
                      type: 'link',
                      text: 'Press 3',
                      href: '/live/press3',
                    },
                    {
                      type: 'link',
                      text: 'Press 4',
                      href: '/live/press4',
                    },
                    {
                      type: 'link',
                      text: 'Press 5',
                      href: '/hmi/press5',
                    },
                  ],
                },
                { type: 'divider' },
                {
                  type: 'section-group',
                  title: 'Process Data',
                  items: [
                    {
                      type: 'link',
                      text: 'Forming',
                      href: '/process-data/forming',
                    },
                    {
                      type: 'link',
                      text: 'Bonding',
                      href: '/process-data/bonding',
                    },
                  ],
                },
                { type: 'divider' },
                {
                  type: 'section-group',
                  title: 'Test Stand',
                  items: [
                    {
                      type: 'link',
                      text: 'Test Stand Dashboard',
                      href: '/test-stand',
                    },
                  ],
                },
                { type: 'divider' },
                {
                  type: 'section-group',
                  title: 'Management',
                  items: [
                    {
                      type: 'link',
                      text: 'All Orders',
                      href: '/orders',
                    },
                    {
                      type: 'link',
                      text: 'Active Orders',
                      href: '/trackers/active',
                    },
                    {
                      type: 'link',
                      text: 'Shipped Orders',
                      href: '/trackers/shipped',
                    },
                    {
                      type: 'link',
                      text: 'Uploads',
                      href: '/parts',
                    },
                    {
                      type: 'link',
                      text: 'Organizations',
                      href: '/organizations',
                    },
                    {
                      type: 'link',
                      text: 'Projects',
                      href: '/projects',
                    },
                    {
                      type: 'link',
                      text: 'Products',
                      href: '/products',
                    },
                    {
                      type: 'link',
                      text: 'Inventory',
                      href: '/inventory',
                    },
                    {
                      type: 'link',
                      text: 'Lots',
                      href: '/lots',
                    },
                    {
                      type: 'link',
                      text: 'QBO Estimates',
                      href: '/qbo/estimates',
                    },
                    {
                      type: 'link',
                      text: 'Create Estimate (Manual)',
                      href: '/qbo/create/estimate',
                    },
                    {
                      type: 'link',
                      text: 'Shipments',
                      href: '/shipments',
                    },
                    {
                      type: 'link',
                      text: 'Print Traveler Labels',
                      href: '/print/travelers',
                    },
                    {
                      type: 'link',
                      text: 'Print Product Label',
                      href: '/print/product-label',
                    },
                    {
                      type: 'link',
                      text: 'Active Orders (Deprecated)',
                      href: '/orders/active',
                    },
                    {
                      type: 'link',
                      text: 'Shipped Orders (Deprecated)',
                      href: '/orders/shipped',
                    },
                  ],
                },
              ]}
            />
          ) : null}
        />
      </div>
    </>

  );
}

function WrappedApp() {
  const app = getApp();
  const functions = getFunctions(app);
  const firestore = getFirestore(app);
  const storage = getStorage(app);

  if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
    // eslint-disable-next-line no-console
    console.log('Connecting to Functions emulator');
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  }
  if (process.env.REACT_APP_USE_FIRESTORE_EMULATOR === 'true') {
    // eslint-disable-next-line no-console
    console.log('Connecting to Firestore emulator');
    connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  }
  if (process.env.REACT_APP_USE_STORAGE_EMULATOR === 'true') {
    // eslint-disable-next-line no-console
    console.log('Connecting to Storage emulator');
    connectStorageEmulator(storage, '127.0.0.1', 9199);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LoginProvider>
        <NotificationsProvider>
          <UserDataProvider>
            <App />
          </UserDataProvider>
        </NotificationsProvider>
      </LoginProvider>
    </QueryClientProvider>
  );
}

export default WrappedApp;
